import { Suspense, useEffect, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GTM_SEARCH_PARAMS_KEYS, handleEventsToGtm } from './utils'
import SessionErrorRoutes from './components/SessionErrorRoutes'
import Loader from './components/Loader'
import WrapperLayout from './components/WrapperLayout'
import MainAppRoutes from './components/MainAppRoutes'
import ThemePublisher from './components/ThemePublisher'
import SelectSchoolYearBookModal from 'components/SelectSchoolYearBookModal'
import ResetPasswordModal from 'containers/ResetPasswordModal'
import StudentAddModal from 'containers/StudentAddModal'
import { indexRoutes } from 'routes'
import { updateUserData } from 'store/actions/user.actions'
import {
  getCurrentUserTypeByRole,
  getUrlParams,
  goToLoginPage,
  isMladinskaPublisher,
} from 'utils'
import {
  useCheckLogin,
  useGetUser,
  useGetUserClassrooms,
  useGetUserConfigurations,
  useGetUserCourses,
  useLogin,
  usePublisherLegalConditions,
} from 'hooks/user'
// eslint-disable-next-line import/default
import 'assets/scss/global.module.scss'
import { fetchStudentListAction } from 'store/actions/student.actions'
import { useGetChildrens } from 'hooks/student'
import { grafanaUtils } from 'utils/Grafana'
import {
  LOGOUT_REDIRECT_URL,
  RELATIVE_EMAIL,
  USER_LANGUAGE,
} from 'utils/constants/localStorage'

const App = () => {
  const { NODE_ENV, REACT_APP_URL_ADMIN } = process.env
  const isDevelop = NODE_ENV === 'development'
  const { errorHandling } = useSelector(({ drawer }) => drawer)
  const [locationPath, setLocationPath] = useState('')
  const { listen } = useHistory()
  const dispatch = useDispatch()
  const {
    i18n: { changeLanguage },
  } = useTranslation()
  const { isIdle, isSuccess: isSuccessLogin, mutate } = useLogin()
  const {
    data: checkLoginData,
    isSuccess: isSuccessCheckLogin,
    isLoading: isLoadingCheckLogin,
    isError: isErrorCheckLogin,
    isFetched,
  } = useCheckLogin(isSuccessLogin || !isDevelop)
  const {
    isSuccess: isSuccessGetUser,
    data: userData,
    isLoading: isLoadingGetUser,
  } = useGetUser(isSuccessCheckLogin && checkLoginData?.loggedIn)
  const { data: publisherData, isLoading: isLoadingPublisher } =
    usePublisherLegalConditions(!!userData?.id)

  const { isAdmin, isRelative } = getCurrentUserTypeByRole(userData?.role)

  const enableConfigurations =
    !!userData?.role && !isAdmin && (isSuccessCheckLogin || isErrorCheckLogin)

  const displayPublisherLegalConditions = publisherData?.publishers?.some(
    (publisher) =>
      !publisher?.legalConditionsAccepted && publisher?.requiredLegalConditions
  )

  const displayEdubookLegalConditions =
    !userData?.eduBookLegalConditionsAccepted
  const { isLoading: isLoadingConfiguration } = useGetUserConfigurations(
    enableConfigurations &&
      !displayEdubookLegalConditions &&
      !displayPublisherLegalConditions
  )

  useGetUserCourses({
    enabled: !!userData?.school && userData?.course === null,
  })
  useGetUserClassrooms({
    enabled:
      !!userData?.school && !!userData?.course && userData?.classroom === null,
  })
  useGetChildrens({
    enabled: displayEdubookLegalConditions
      ? isRelative &&
        !displayEdubookLegalConditions &&
        !displayPublisherLegalConditions
      : isRelative,
  })

  const isLoading =
    isLoadingCheckLogin ||
    isLoadingGetUser ||
    isLoadingPublisher ||
    (displayEdubookLegalConditions
      ? !displayEdubookLegalConditions
      : isLoadingConfiguration)

  useEffect(() => {
    grafanaUtils.setUser({})
    if (isDevelop && isIdle) {
      mutate()
    }
  }, [])

  useEffect(() => {
    if (isFetched && !checkLoginData?.loggedIn) {
      goToLoginPage()
    }
  }, [isFetched, isErrorCheckLogin])

  useEffect(() => {
    if (isSuccessGetUser) {
      const isMladinska = isMladinskaPublisher(userData.publishers)
      localStorage.setItem(
        LOGOUT_REDIRECT_URL,
        isMladinska ? window.def.mladinskaPublicUrl : window.origin
      )
      const studentIdParam = getUrlParams(GTM_SEARCH_PARAMS_KEYS.CHILD)
      const utmSourceParam = getUrlParams(GTM_SEARCH_PARAMS_KEYS.UTM_SOURCE)
      grafanaUtils.setUser({
        email: userData.email,
        role: userData.role,
        userId: userData.id,
        username: userData.username,
      })
      handleEventsToGtm({
        isValidated: !utmSourceParam,
        role: userData.role,
        studentId: studentIdParam,
        userId: userData.id,
      })
      if (isAdmin) {
        if (isDevelop) {
          window.location.href = REACT_APP_URL_ADMIN
        } else {
          window.location.href = '/admin'
        }
      }
      dispatch(updateUserData(userData))
      if (isRelative && !displayEdubookLegalConditions) {
        dispatch(fetchStudentListAction()) // WIP: remove when you do not have components with classes
      }
      localStorage.setItem(USER_LANGUAGE, userData.locale)
      changeLanguage(userData.locale)
      localStorage.removeItem(RELATIVE_EMAIL)
    }
  }, [isSuccessGetUser, userData])

  useEffect(
    () =>
      listen((location) => {
        if (location.pathname !== locationPath) {
          setLocationPath(location.pathname)
        }
      }),
    [locationPath]
  )

  const userRoutes = indexRoutes.filter(({ urlRole }) =>
    urlRole.includes(userData?.role)
  )

  if (isLoading || !userRoutes.length) {
    return <Loader />
  }

  if (errorHandling) {
    return <SessionErrorRoutes />
  }

  return (
    <ThemePublisher publishers={userData?.publishers}>
      <Suspense fallback={<Loader />}>
        <WrapperLayout>
          <MainAppRoutes
            displayEdubookLegalConditions={displayEdubookLegalConditions}
            displayPublisherLegalConditions={displayPublisherLegalConditions}
            userResetPassword={userData?.userFlags?.FORCE_RESET_PASSWORD}
            userRole={userData?.role}
          />
        </WrapperLayout>
        {userData?.role && !isAdmin && (
          <>
            <ResetPasswordModal />
            <SelectSchoolYearBookModal />
            {
              //WIP rebranding add-student
              <StudentAddModal />
            }
          </>
        )}
      </Suspense>
    </ThemePublisher>
  )
}

export default App
